import LoadingLottie from "components/lottieLoading/LoadingLottie";
import AuthLayout from "pages/layouts/AuthLayout";
import HomeLayout from "pages/layouts/HomeLayout";
import React, { ComponentType, lazy, Suspense, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { HomeProps } from "types/v2/Home/HomeProps";

const OpenNavigationPopupPage = lazy(
  () => import("utils/mapModals/OpenNavigationPopup"),
);

const HomePage: React.FC<HomeProps> = lazy(
  () => import("pages/Home/BottomTab/Home"),
);
const TrackerPage = lazy(() => import("pages/Home/BottomTab/Tracker"));

const FactorsPage = lazy(() => import("pages/V2/Factors/Factors"));
const FactorPage = lazy(() => import("pages/V2/FactorDetail/FactorDetail"));
const ServicesPage = lazy(() => import("pages/Home/BottomTab/Services"));
const SettingsPage: any = lazy(() => import("pages/Home/menubar/Settings"));
const ReportsPage: ComponentType = lazy(
  () => import("pages/Home/menubar/ReportPages/Reports"),
);
const StopReportPage: ComponentType = lazy(
  () => import("pages/Home/menubar/ReportPages/StopReport"),
);
const NotificationPage: ComponentType = lazy(
  () => import("pages/Home/notification/Noification"),
);
const NotificationFilterPage: ComponentType = lazy(
  () => import("pages/Home/notification/FilterNotification"),
);
const SpeedReportPage: ComponentType = lazy(
  () => import("pages/Home/menubar/ReportPages/SpeedReport"),
);
const FuelReportPage: ComponentType = lazy(
  () => import("pages/Home/menubar/ReportPages/FuelReport"),
);
const MovingReportPage: ComponentType = lazy(
  () => import("pages/Home/menubar/ReportPages/MovingReport"),
);
const NavigationReportPage: ComponentType = lazy(
  () => import("pages/Home/menubar/ReportPages/NavigationReport"),
);
const PowerReportPage: ComponentType = lazy(
  () => import("pages/Home/menubar/ReportPages/PowerReport"),
);
const StandbyReportPage: ComponentType = lazy(
  () => import("pages/Home/menubar/ReportPages/StandbyReport"),
);
const GeoReportPage: ComponentType = lazy(
  () => import("pages/Home/menubar/ReportPages/GeoReport"),
);
const ProfilePage: ComponentType = lazy(
  () => import("pages/Home/Profile/Profile"),
);

const LoginTouchPage: ComponentType = lazy(
  () => import("pages/Auth/LoginTouch"),
);

const LoginDrawPage: ComponentType = lazy(() => import("pages/Auth/LoginDraw"));
const TrackerSettingsPage: ComponentType = lazy(
  () => import("pages/Home/BottomTab/TrackerSettings"),
);
const ChatPage: ComponentType = lazy(
  () => import("pages/Home/notification/Chat"),
);
const ChatMapPage: ComponentType = lazy(
  () => import("pages/Home/notification/ChatMap"),
);
const AddTrackerPage: ComponentType = lazy(
  () => import("utils/trackerModal/AddTrackerPage"),
);
const EditTrackerPage: ComponentType = lazy(
  () => import("utils/trackerModal/EditTrackerPage"),
);

const LocationHistoryMapPage: ComponentType = lazy(
  () => import("pages/Home/BottomTab/maps/LocationHistoryMap"),
);

const LimitationsPage: any = lazy(
  () => import("pages/Home/menubar/Limitations"),
);
const LimitationsEditPage: any = lazy(
  () => import("utils/LimitationModal/EditLimitPage"),
);
const LimitationsAddPage: any = lazy(
  () => import("utils/LimitationModal/AddLimitPage"),
);
const SessionsPage: any = lazy(() => import("pages/Home/menubar/Sessions"));
const LoginPage: ComponentType = lazy(() => import("pages/Auth/Login"));
const SplashPage: any = lazy(() => import("pages/Auth/Splash"));
const SignupPage: ComponentType = lazy(() => import("pages/Auth/Signup"));
const VerifyPage: ComponentType = lazy(() => import("pages/Auth/Verify"));
const ForgetPasswordPage: ComponentType = lazy(
  () => import("pages/Auth/ForgetPassword"),
);
const LoginForgetPasswordPage: ComponentType = lazy(
  () => import("pages/Auth/LoginForgetPassword"),
);

const LoginFromBo: ComponentType = lazy(() => import("pages/Auth/LoginFromBo"));
const SupportPage: ComponentType = lazy(
  () => import("pages/V2/Support/Support"),
);
const PlansPage: ComponentType = lazy(() => import("pages/V2/Plans/Plans"));
const PaymentPage: ComponentType = lazy(
  () => import("pages/V2/Payment/Payment"),
);
const OnlineCommandsPage: ComponentType = lazy(
  () => import("pages/V2/OnlineCommands/OnlineCommands"),
);
const QuickLoginRedirectPage: ComponentType = lazy(
  () => import("pages/Auth/QuickLoginRedirect"),
);

const OtherAlertsPage: any = lazy(
  () => import("pages/V2/OnlineCommands/OtherAlerts"),
);

const OtherSettingsPage: any = lazy(
  () => import("pages/V2/OnlineCommands/OtherSettings"),
);

const TouchLoginRoute = ({ children }: any) => {
  const shouldTouch = localStorage.getItem("shouldTouch");
  const token = localStorage.getItem("token");

  if (shouldTouch) {
    return token ? children : <Navigate to="/auth" replace />;
  }

  return token ? (
    <Navigate to="/home" replace />
  ) : (
    <Navigate to="/auth" replace />
  );
};

const AppRoute = ({ children }: any) => {
  const shouldTouch = localStorage.getItem("shouldTouch");
  const touchLogin = localStorage.getItem("touchLogin");
  const token = localStorage.getItem("token");

  if (shouldTouch) {
    return <Navigate to="/login-touch" replace />;
  }

  if (touchLogin) {
    if (new Date().getTime() - new Date(touchLogin).getTime() < 60000) {
      const now = new Date();
      localStorage.setItem("touchLogin", now.toISOString());
    }
  }

  return token ? children : <Navigate to="/auth" replace />;
};
const AuthRoute = ({ children }: any) => {
  const token = localStorage.getItem("token");
  return !token ? children : <Navigate to="/home" replace />;
};

const Router = ({ theme, switchTheme, rotated, checkFcm, getFcmAPK }: any) => {
  const token = localStorage.getItem("token");
  const [isTrackerStatusModalOpen, setIsTrackerStatusModalOpen] =
    useState<boolean>(false);
  const [showMenBar, setShowMenuBar] = useState<boolean>(false);
  if (!token) {
    return (
      <AuthLayout>
        <Suspense fallback={null}>
          <Routes>
            <Route
              path="/"
              element={
                <AuthRoute>
                  <SplashPage url="/auth" />
                </AuthRoute>
              }
            />
            <Route
              path="/auth"
              element={
                <AuthRoute>
                  <LoginPage />
                </AuthRoute>
              }
            />
            <Route
              path="/auth/signup"
              element={
                <AuthRoute>
                  <SignupPage />
                </AuthRoute>
              }
            />
            <Route
              path="/auth/verify"
              element={
                <AuthRoute>
                  <VerifyPage />
                </AuthRoute>
              }
            />
            <Route
              path="/auth/forget-password"
              element={
                <AuthRoute>
                  <ForgetPasswordPage />
                </AuthRoute>
              }
            />
            <Route path="/auth/from-bo" element={<LoginFromBo />} />
            <Route
              path="/auth/login-forget-password"
              element={
                <AuthRoute>
                  <LoginForgetPasswordPage />
                </AuthRoute>
              }
            />
            <Route path="/r/*" element={<QuickLoginRedirectPage />} />
            <Route path="/contact-us" element={<SupportPage />} />
            <Route path="*" element={<Navigate to="/auth" replace />} />
            <Route path="/pay" element={<PaymentPage />} />
          </Routes>
        </Suspense>
      </AuthLayout>
    );
  } else {
    return (
      <HomeLayout
        isTrackerStatusModalOpen={isTrackerStatusModalOpen}
        setIsTrackerStatusModalOpen={setIsTrackerStatusModalOpen}
        showMenuBar={showMenBar}
        setShowMenuBar={setShowMenuBar}
      >
        <Suspense fallback={rotated ? null : <LoadingLottie />}>
          <Routes>
            <Route
              path="/"
              element={
                <AuthRoute>
                  <SplashPage url="/home" />
                </AuthRoute>
              }
            />
            <Route
              path="/home"
              element={
                <AppRoute>
                  <HomePage
                    isTrackerStatusModalOpen={isTrackerStatusModalOpen}
                    setIsTrackerStatusModalOpen={setIsTrackerStatusModalOpen}
                    showMenuBar={showMenBar}
                  />
                </AppRoute>
              }
            />
            <Route
              path="/trackers"
              element={
                <AppRoute>
                  <TrackerPage theme={theme} />
                </AppRoute>
              }
            />
            <Route
              path="/services"
              element={
                <AppRoute>
                  <ServicesPage theme={theme} />
                </AppRoute>
              }
            />
            <Route
              path="/transactions"
              element={
                <AppRoute>
                  <FactorsPage />
                </AppRoute>
              }
            />
            <Route
              path="/transactions/factor/:id"
              element={
                <AppRoute>
                  <FactorPage />
                </AppRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <AppRoute>
                  <SettingsPage
                    getFcmAPK={getFcmAPK}
                    checkFcm={checkFcm}
                    getTheme={theme}
                    switchTheme={switchTheme}
                  />
                </AppRoute>
              }
            />
            <Route
              path="/online-commands/:IMEI"
              element={<OnlineCommandsPage />}
            />
            <Route path="/other-alerts/:IMEI" element={<OtherAlertsPage />} />
            <Route
              path="/other-settings/:IMEI"
              element={<OtherSettingsPage />}
            />
            <Route
              path="/settings/sessions"
              element={
                <AppRoute>
                  <SessionsPage theme={theme} />
                </AppRoute>
              }
            />
            <Route
              path="/limitations"
              element={
                <AppRoute>
                  <LimitationsPage theme={theme} />
                </AppRoute>
              }
            />
            <Route
              path="/limitations/edit"
              element={
                <AppRoute>
                  <LimitationsEditPage theme={theme} />
                </AppRoute>
              }
            />
            <Route
              path="/limitations/add"
              element={
                <AppRoute>
                  <LimitationsAddPage theme={theme} />
                </AppRoute>
              }
            />
            <Route
              path="/notification"
              element={
                <AppRoute>
                  <NotificationPage />
                </AppRoute>
              }
            />
            <Route
              path="/notification/chat/:id/filter"
              element={
                <AppRoute>
                  <NotificationFilterPage />
                </AppRoute>
              }
            />
            <Route
              path="/reports"
              element={
                <AppRoute>
                  <ReportsPage />
                </AppRoute>
              }
            />
            <Route
              path="/reports/stop"
              element={
                <AppRoute>
                  <StopReportPage />
                </AppRoute>
              }
            />
            <Route
              path="/reports/speed"
              element={
                <AppRoute>
                  <SpeedReportPage />
                </AppRoute>
              }
            />
            <Route
              path="/reports/fuel"
              element={
                <AppRoute>
                  <FuelReportPage />
                </AppRoute>
              }
            />
            <Route path="/r/*" element={<QuickLoginRedirectPage />} />
            <Route
              path="/reports/geo"
              element={
                <AppRoute>
                  <GeoReportPage />
                </AppRoute>
              }
            />
            <Route
              path="/reports/navigation"
              element={
                <AppRoute>
                  <NavigationReportPage />
                </AppRoute>
              }
            />
            <Route
              path="/reports/standby"
              element={
                <AppRoute>
                  <StandbyReportPage />
                </AppRoute>
              }
            />
            <Route
              path="/reports/moving"
              element={
                <AppRoute>
                  <MovingReportPage />
                </AppRoute>
              }
            />
            <Route
              path="/reports/power"
              element={
                <AppRoute>
                  <PowerReportPage />
                </AppRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <AppRoute>
                  <ProfilePage />
                </AppRoute>
              }
            />
            <Route
              path="/login-touch"
              element={
                <TouchLoginRoute>
                  <LoginTouchPage />
                </TouchLoginRoute>
              }
            />
            <Route
              path="/login-draw"
              element={
                <AppRoute>
                  <LoginDrawPage />
                </AppRoute>
              }
            />
            <Route path="/contact-us" element={<SupportPage />} />
            <Route
              path="/trackers/settings/:model/:imei"
              element={
                <AppRoute>
                  <TrackerSettingsPage />
                </AppRoute>
              }
            />
            <Route
              path="/location-history"
              element={
                <AppRoute>
                  <LocationHistoryMapPage />
                </AppRoute>
              }
            />
            <Route
              path="/add-tracker"
              element={
                <AppRoute>
                  <AddTrackerPage />
                </AppRoute>
              }
            />
            <Route
              path="/trackers/:id"
              element={
                <AppRoute>
                  <EditTrackerPage />
                </AppRoute>
              }
            />
            <Route
              path="/notification/chat/:id"
              element={
                <AppRoute>
                  <ChatPage />
                </AppRoute>
              }
            />
            <Route
              path="/notification/chat/map"
              element={
                <AppRoute>
                  <ChatMapPage />
                </AppRoute>
              }
            />
            <Route
              path="/navigation-popup"
              element={
                <AppRoute>
                  <OpenNavigationPopupPage />
                </AppRoute>
              }
            />
            <Route
              path="/plans"
              element={
                <AppRoute>
                  <PlansPage />
                </AppRoute>
              }
            />
            <Route path="/auth/from-bo" element={<LoginFromBo />} />
            <Route path="/pay" element={<PaymentPage />} />
            <Route path="*" element={<Navigate to="/home" replace />} />
          </Routes>
        </Suspense>
      </HomeLayout>
    );
  }
};

export default Router;
